import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../services/api';

const AuthContext = createContext(null);

function isClientFacingPath(pathname) {
  return /^\/(chat|client)\//.test(pathname || '');
}

export function AuthProvider({ children }) {
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  // Keep agent JWT off shared axios defaults on buyer/seller chat and client portal routes.
  useEffect(() => {
    if (isClientFacingPath(location.pathname)) {
      delete api.defaults.headers.common.Authorization;
      setLoading(false);
      return;
    }
    const token = localStorage.getItem('token');
    if (token) {
      api.defaults.headers.common.Authorization = `Bearer ${token}`;
      api.get('/auth/me')
        .then(res => setAgent(res.data.agent))
        .catch(() => localStorage.removeItem('token'))
        .finally(() => setLoading(false));
    } else {
      delete api.defaults.headers.common.Authorization;
      setLoading(false);
    }
  }, [location.pathname]);

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    const { token, agent } = res.data;
    localStorage.setItem('token', token);
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setAgent(agent);
    return agent;
  };

  const register = async (payload) => {
    const res = await api.post('/auth/register', payload);
    const { token, agent } = res.data;
    localStorage.setItem('token', token);
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setAgent(agent);
    return agent;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete api.defaults.headers.common['Authorization'];
    setAgent(null);
  };

  const refreshAgent = async () => {
    const res = await api.get('/auth/me');
    setAgent(res.data.agent);
    return res.data.agent;
  };

  const patchAgent = (partial) => {
    setAgent((prev) => (prev ? { ...prev, ...partial } : prev));
  };

  // login/logout/refreshAgent/patchAgent only close over stable setters and
  // module-level api/localStorage, so agent + loading are the only real deps.
  const value = useMemo(
    () => ({ agent, loading, login, register, logout, refreshAgent, patchAgent }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [agent, loading]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);